import { DraftFieldsInterface, FieldsInterface } from '../../Interfaces/Draft/FieldsInterface';
import { PaymentMeanInterface } from '../../Interfaces/Settings/PaymentMeanInterface';
import { InvoicerInterface } from '../../Interfaces/Draft/DraftInterface';
import { getLocale } from '../../Other/Configs';
import Immutable from '../../Other/Immutable';
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface';
import { TokenInterface } from '../../Interfaces/TokenInterface';
import { DocumentType, DocumentTypes, DraftInterface } from '../../Interfaces/Draft/DraftInterface';
import { PaymentMeans } from '../../Interfaces/Settings/PaymentMeanInterface';
import { CompanyInterface } from '../../Interfaces/CompanyInterface';
import { StateInterface } from '../../Interfaces/StateInterface';
import { calculateDaysFromStartDate } from '../../Other/functions/dateFunctions';
import * as moment from 'moment';
import { ModelInterface } from '../../Interfaces/Settings/ModelInterface';
import { CustomerInterface } from '../../Interfaces/CustomerInterface';
import { LineInterface } from '../../Interfaces/Draft/LineInterface';
import { compactObject, flattenObject } from '../../Other/Object';
import { l } from '../../Other/Translation';
import { Templates } from '../../Other/Templates';

const DEFAULT_PAYMENT_TIME = 30;

export const defaultLine = (settings: SettingsInterface): LineInterface => {
  const invoiceModel = settings.models.find((m) => m.type === 'invoice');
  const defaultCurrency = invoiceModel.fields.find((f) => f.name === 'currency');
  let currency = 'EUR';
  if (defaultCurrency) {
    currency = defaultCurrency.default as string;
  }
  return {
    type: 'row',
    description: '',
    quantity: 1,
    unitPrice: 0,
    vatPct: settings.values.company.vat ? settings.values.company.vat : 0,
    taxTotal: 0,
    netTotal: 0,
    grossTotal: 0,
    unit: 'h',
    currency
  };
};

export const isValidPaymentMean = (pm: PaymentMeanInterface): boolean => {
  const { fields } = pm;
  if (!fields) {
    return false;
  }
  const valid = Object.keys(fields).some((f): boolean => {
    return typeof fields[f] !== 'undefined' && fields[f].length > 0;
  });
  return valid;
};

export const getDefaultLocale = (state: StateInterface, documentType: DocumentType): string => {
  const model = getModel(state.settings, documentType);
  const language = model.fields.filter((f) => {
    return f.name === 'language';
  })[0].default as string;
  return getLocale(state.config, language, state.country);
};

export const extractFieldsFromSettings = (settings: SettingsInterface,
  locale: string,
  documentType: DocumentType) => {
  const model = getModel(settings, documentType);
  const fields: { [key: string]: any } = {};
  model.fields.forEach((field) => {
    fields[field.name] = {
      visible: field.visible,
      name: field.name,
      ...(field.name === 'columns' ? { value: field.default } : {})
    };
    if (typeof (field.default) !== 'undefined') {
      switch (field.name) {
        case 'type':
        case 'paymentTime':
          fields[field.name].value = field.default ? parseInt(String(field.default), 10) : DEFAULT_PAYMENT_TIME;
          break;
        case 'interest':
          fields[field.name].value = parseFloat(String(field.default));
          break;
        case 'discountEnabled':
          fields[field.name].value = (field.default === 'true');
          break;
        case 'template':
          fields[field.name].value = Templates.getTemplates(getCountry(locale))[0];
          break;
        default:
          fields[field.name].value = field.default;
      }

      if (field.name === 'referenceNumber' && !(/^[0-9]{1,32}$/.test(String(field.default)))) {
        fields[field.name].value = '';
      }

      if (field.name === 'recipientReference') {
        // ignore the value coming from settings
        fields[field.name].value = '';
      }
    }
  });
  return fields;
};

export const getDefaultCustomer = ({ customers }: StateInterface): CustomerInterface | null => {
  const hasOneCustomer = customers && customers.collection && customers.collection.length === 1;
  return hasOneCustomer ? Immutable.copy(customers.collection[0]) : null;
};

export const getPaymentMeans = (settings: SettingsInterface) => {
  const paymentMeans: any = Immutable.copy(settings.values.paymentMethods)
    .filter(isValidPaymentMean)
    .map((pm) => {
      const { type, fields } = pm;
      return { type, fields };
    });
  for (const method of paymentMeans) {
    delete (method._id);
  }
  return paymentMeans;
};

export const getModel = (settings: SettingsInterface, document: DocumentType): ModelInterface => {
  return settings.models.filter((model) => {
    return model.type === document;
  })[0];
};

export const getTradeName = (settings: SettingsInterface, documentType: DocumentType) => {
  const model = getModel(settings, documentType);
  // always first check the default === true, otherwise take the sender one
  // if it still failes take the first one as default
  const tradeNames = settings.values.company.tradeNames;
  const senderField = model.fields.filter((f) => {
    return f.name === 'sender';
  })[0];
  const sender = senderField && senderField['default'];
  let tradeName;
  let senderName;
  if (tradeNames && tradeNames.length > 1) {
    tradeNames.forEach((name) => {
      if (name.default === true) {
        tradeName = name;
      }
      if (name._id === sender) {
        senderName = name;
      }
    });

    if (!tradeName && senderName) {
      tradeName = senderName;
    }

    if (!tradeName) {
      tradeName = tradeNames[0];
    }
  } else {
    tradeName = tradeNames[0];
  }
  return tradeName;
};

export const makeMandatoryFieldsVisible = (fields: DraftFieldsInterface,
  settings: SettingsInterface,
  document: DocumentType): DraftFieldsInterface => {
  const model = getModel(settings, document);
  const schema = settings.schemas[model.type];
  // Make all mandatory fields always visible
  const excludeFields = ['discount'];
  schema.fields.forEach((field) => {
    if (excludeFields.indexOf(field.name) >= 0) {
      return;
    }
    if (!fields[field.name]) {
      fields[field.name] = {
        name: field.name
      };
    }
    if (field.mandatory) {
      fields[field.name].visible = true;
    }
  });

  return Immutable.copy(fields);
};

export const getFields = (document: DocumentType,
  settings: SettingsInterface,
  locale: string): DraftFieldsInterface => {
  const fields = extractFieldsFromSettings(settings, locale, document);
  const now = moment();
  let then = moment();
  const defaultTemplateName = 'default';
  const templateName = (fields.template && fields.template.value) || defaultTemplateName;
  for (const name in fields) {
    const field = fields[name];
    switch (name) {
      case 'paymentTime':
        then = calculateDaysFromStartDate(now, parseInt(field.value, 10));
        break;
      case 'columns':
        field.value = field.value.split(',');
        break;
    }
  }

  let updatedFields: DraftFieldsInterface = {
    title: {
      name: 'title',
      value: 'Invoice'
    },
    template: {
      name: 'template',
      value: templateName,
    },
    footerText: {
      name: 'footerText',
      value: ''
    },
    // Discount fields
    discountEnabled: {
      name: 'discountEnabled',
      value: false
    },
    discountLocation: {
      name: 'discountLocation',
      value: 'perTotal'
    },
    discountAmount: {
      name: 'discountAmount',
      value: 0
    },
    discountType: {
      name: 'discountType',
      value: 'amount'
    },
    discountVAT: {
      name: 'discountVAT',
      value: 'postVAT'
    },
    ...(Immutable.copy(fields) as FieldsInterface),
    // Also add some fields that are missing from settings
    invoiceNumber: {
      name: 'invoiceNumber',
      visible: true,
      value: '1'
    },
    issueDate: {
      name: 'issueDate',
      value: now.toISOString()
    },
    locale: {
      name: 'locale',
      value: locale
    },
    deduction: {
      name: 'deduction',
      value: 0,
      visible: false
    },
    recurringProfileEnabled: {
      name: 'recurringProfileEnabled',
      visible: false,
      value: false
    }
  };
  if (updatedFields.dueDate) {
    updatedFields.dueDate.value = then.toISOString();
  }
  const isPaymentTime = updatedFields.paymentTime && updatedFields.paymentTime.visible;
  const isDueDate = updatedFields.dueDate && updatedFields.dueDate.visible;
  const defaultDueDate = calculateDaysFromStartDate(now, DEFAULT_PAYMENT_TIME).toISOString();
  if (isDueDate && !updatedFields.dueDate.value) {
    fields.dueDate.value = defaultDueDate;
  }
  if (!isPaymentTime && !isDueDate && document !== DocumentTypes.ESTIMATE) {
    // make due date visible and set default value
    if (!updatedFields.dueDate) {
      updatedFields.dueDate = {
        name: 'dueDate',
        visible: true,
        value: defaultDueDate,
      };
    } else {
      updatedFields.dueDate.visible = true;
      updatedFields.dueDate.value = defaultDueDate;
    }
  }
  updatedFields = makeMandatoryFieldsVisible(updatedFields, settings, document);
  if (updatedFields.template.value.indexOf('.') === -1) {
    updatedFields.template.value = getCountry(locale) + '.' + updatedFields.template.value;
  }
  return updatedFields;
};

export const getSummary = (currency) => {
  return {
    currency: currency,
    grossTotal: 0,
    netTotal: 0,
    taxTotal: 0,
    workTotal: 0,
    reductionTotal: 0,
    discountTotal: 0,
    taxSummary: []
  };
};

export const getStyles = (settings: SettingsInterface,
  country: string,
  fields: DraftFieldsInterface,
  document: DocumentType) => {

  const defaultTemplateName = 'default';
  const defaultStyles = (settings.styles[document] &&
    settings.styles[document][defaultTemplateName]) ||
    settings.styles.invoice[defaultTemplateName];

  if (fields.template && settings.styles[document] && settings.styles[document][fields.template.value.substr(3)]) {
    return settings.styles[document][fields.template.value.substr(3)];
  } else {
    return defaultStyles;
  }
};

export const getInvoicer = (settings: SettingsInterface,
  company: CompanyInterface,
  { country }: TokenInterface,
  documentType: DocumentType): InvoicerInterface => {
  const tradeName = getTradeName(settings, documentType);
  const { rcs = '', legalStatus = '', hrNr, amtsgericht, taxPrepaymentRegistered } = settings.values.company;
  const hrNrProp = hrNr ? { hrNr } : {};
  const amtsgerichtProp = amtsgericht ? { amtsgericht } : {};
  const ftaxProp = typeof taxPrepaymentRegistered === 'boolean' ? { taxPrepaymentRegistered } : {};
  const invoicer: InvoicerInterface = {
    tradeName: { ...tradeName, country },
    vatNumber: company.vatNumber,
    businessId: company.businessId,
    rcs,
    legalStatus,
    id: company.id,
    ...hrNrProp,
    ...amtsgerichtProp,
    ...ftaxProp
  };
  return invoicer;
};

export const getCountry = (locale) => locale.substr(3, 2);

interface InvoiceStylesInterface {
  [key: string]: { [key: string]: string | number };
}

export const defaultDocument = (document = DocumentTypes.INVOICE,
  settings,
  locale: string): DraftInterface => {
  if (!settings) { return null; }
  const model = getModel(settings, document);
  const fields = getFields(document, settings, locale);
  const styles = {}; // getStyles(settings, country, fields, document) as InvoiceStylesInterface;
  const paymentMeans = getPaymentMeans(settings);
  const receiver = null;
  const summary = getSummary(fields.currency.value);
  const invoicer = null;
  const invoice: DraftInterface = {
    id: 0,
    invoicer,
    receiver,
    fields,
    summary,
    styles,
    paymentMeans,
    document: model.type as DocumentType,
    model: model.name,
    lines: [defaultLine(settings)],
    invalidFields: [],
    isDirty: false
  };
  return invoice;
};

export const isInvoiceNumberNewSequence = (draft, sequences) => {
  const { invoiceNumber } = draft;
  if (!invoiceNumber || !invoiceNumber.prefix) {
    return false;
  }
  return !sequences.all.find((s) => s.prefix === draft.invoiceNumber.prefix);
};

export const hasMinimumInfo = (draft: DraftInterface) => {
  // This is for special purpose of determining if sender info has been filled at all
  // so that the dummy sender info can be shown in the preview
  draft = Immutable.copy(draft);
  // these fields are coming from system, so ignore.
  // having tradeName.name is not enough so ignore that too
  delete draft.invoicer.id;
  delete draft.invoicer.tradeName._id;
  delete draft.invoicer.tradeName.default;
  delete draft.invoicer.tradeName.country;
  delete draft.invoicer.tradeName.name;
  delete draft.invoicer.taxPrepaymentRegistered;

  const invoicerInfo = Object.values(compactObject(flattenObject(draft.invoicer))).join('');
  const paymentInfo = draft.paymentMeans ? draft.paymentMeans.reduce((acc, cur) => {
    return [...acc, ...Object.values(compactObject(flattenObject(cur.fields)))];
  }, []).join('') : '';
  return invoicerInfo || paymentInfo;
};

export const getDummyInvoicer = (draft: DraftInterface) => {
  return {
    id: draft.invoicer.id,
    tradeName: {
      ...draft.invoicer.tradeName,
      address: {
        streetAddress1: l('exampleInvoicer.streetAddress1'),
        streetAddress2: l('exampleInvoicer.streetAddress2'),
        pobox: l('exampleInvoicer.pobox'),
        www: l('exampleInvoicer.www'),
        city: l('exampleInvoicer.city')
      },
      contact: {
        name: { full: l('exampleInvoicer.contactPersonFullName'), last: '', first: '' },
        phone: l('exampleInvoicer.contactPhone'),
        email: l('exampleInvoicer.contactEmail')
      },
      logo: { id: 'false', content: require('../../Static/images/placeholder_logo.png'), name: 'LOGO' },
      name: draft.invoicer.tradeName.name || l('exampleInvoicer.tradeName')
    },
    vatNumber: null
  };
};

export const getDummyPaymentInfo = (settings: SettingsInterface) => {
  return Object.keys(settings.schemas.paymentMethods).map((type) => {
    if (!settings.schemas.paymentMethods[type].enabled) { return null; }
    switch (type.toUpperCase()) {
      case PaymentMeans.PAYPAL:
        return { type: PaymentMeans.PAYPAL, fields: { paypal: l('examplePaymentMethod.paypal') } };
      case PaymentMeans.PLUSGIRO:
        return { type: PaymentMeans.PLUSGIRO, fields: { plusgiro: l('examplePaymentMethod.plusgiro') } };
      case PaymentMeans.BANKGIRO:
        return { type: PaymentMeans.BANKGIRO, fields: { bankgiro: l('examplePaymentMethod.bankgiro') } };
      case PaymentMeans.SWISH:
        return { type: PaymentMeans.SWISH, fields: { swish: l('examplePaymentMethod.swish') } };
      case PaymentMeans.OTHER:
        return { type: PaymentMeans.OTHER, fields: { free: l('examplePaymentMethod.other') } };
      case PaymentMeans.IBAN:
        return {
          type: PaymentMeans.IBAN, fields: {
            bank: l('examplePaymentMethod.bank'),
            iban: l('examplePaymentMethod.iban'),
            swift: l('examplePaymentMethod.swift'),
          }
        };
      default:
        return null;
    }
  }).filter((pm) => pm); // filter nulls
};
